import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_SOURCES_STATS } from '../../../../query/monitor/data/sources/keys';
import { getSourcesStats } from '../../../../../services/monitor/data/sources/MonitorDataSourcesService';
import { ChatType } from '../../../../../types/monitor/ChatType';
import { SourcesStatsResponse } from '../../../../../models/monitor/data/sources/SourcesStatsResponse';

export const useGetSourceStats = (
  projectId: number | null,
  chatType: ChatType | null,
  from: Date | null,
  to: Date | null,
  page?: number,
  pageSize?: number,
  domain?: string[] | null | undefined,
  keyword?: string,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
) => {
  const queryClient = useQueryClient();
  const queryEnabled = !!projectId && !!chatType && !!from && !!to && domain !== null;

  const fromTimestamp = from?.getTime() ?? null;
  const toTimestamp = to?.getTime() ?? null;

  const {
    data: sourcesStats,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<SourcesStatsResponse>({
    queryKey: [
      GET_SOURCES_STATS,
      projectId,
      chatType,
      fromTimestamp,
      toTimestamp,
      page,
      pageSize,
      domain,
      keyword,
      monitorPromptIds,
      filterMonitorTypes,
    ],
    queryFn: () => getSourcesStats(projectId!, chatType!, from, to, page, pageSize, domain, keyword, monitorPromptIds, filterMonitorTypes),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: queryEnabled,
  });

  const empty: SourcesStatsResponse = { sources: [] };

  return {
    sourcesStats: queryEnabled ? sourcesStats ?? empty : empty,
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: [
          GET_SOURCES_STATS,
          projectId,
          chatType,
          fromTimestamp,
          toTimestamp,
          page,
          pageSize,
          domain,
          keyword,
          monitorPromptIds,
          filterMonitorTypes,
        ],
        refetchType: 'active',
      }),
  };
};
